import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Globe } from 'lucide-react';
import CompanyStats from '../components/CompanyStats';
import ProfessionalServices from '../components/ProfessionalServices';
import SEO from '../components/SEO';

const ServicesContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a0033 30%, #330066 60%, #000000 100%);
  padding-top: 0;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 60px 20px;
  color: white;
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #fff 0%, #87ceeb 50%, #fff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.3rem;
  margin-bottom: 60px;
  opacity: 0.9;
  line-height: 1.6;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ServiceCard = styled(motion.div)`
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(135,206,235,0.3);
  padding: 40px 30px;
  border-radius: 25px;
  text-align: center;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      rgba(255,153,51,0.1) 0%, 
      rgba(255,255,255,0.1) 50%, 
      rgba(19,136,8,0.1) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 0;
  }
  
  &:hover {
    transform: translateY(-15px) scale(1.02);
    border-color: rgba(135,206,235,0.6);
    box-shadow: 
      0 20px 40px rgba(0,0,0,0.2),
      0 0 30px rgba(135,206,235,0.3),
      inset 0 1px 0 rgba(255,255,255,0.2);
    
    &::before {
      opacity: 1;
    }
  }
  
  > * {
    position: relative;
    z-index: 1;
  }
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #87ceeb 0%, #1e3a8a 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 15px;
  color: #87ceeb;
  font-weight: 600;
`;

const ServiceDescription = styled.p`
  color: #ffffff;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  text-align: left;
  
  li {
    color: rgba(255,255,255,0.8);
    margin-bottom: 8px;
    padding-left: 20px;
    position: relative;
    
    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: #87ceeb;
      font-weight: bold;
    }
  }
`;

const Services = () => {
  const services = [
    {
      icon: <Globe size={40} />,
      title: "E-commerce Solutions",
      description: "Complete e-commerce platforms and solutions to help you sell online and grow your digital business.",
      features: [
        "Custom E-commerce Development",
        "Payment Gateway Integration",
        "Inventory Management",
        "Multi-vendor Platforms",
        "Mobile Commerce"
      ]
    }
  ];

  return (
    <ServicesContainer>
      <SEO
        title="Services | InitX Technologies"
        description="Explore InitX Technologies services: AI development, cloud solutions, web and mobile app development, DevOps, and digital transformation for startups and enterprises."
        keywords="AI development services, cloud solutions, web development, mobile app development, DevOps, cybersecurity, IT consulting, software engineering"
        type="website"
        siteName="InitX Technologies"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' }
        ]}
      />
      <ProfessionalServices />

      <CompanyStats />

      <ServicesGrid>
        {services.map((service, index) => (
          <ServiceCard
            key={index}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
          >
            <ServiceIcon>{service.icon}</ServiceIcon>
            <ServiceTitle>{service.title}</ServiceTitle>
            <ServiceDescription>{service.description}</ServiceDescription>
            <ServiceFeatures>
              {service.features.map((feature, idx) => (
                <li key={idx}>{feature}</li>
              ))}
            </ServiceFeatures>
          </ServiceCard>
        ))}
      </ServicesGrid>
    </ServicesContainer>
  );
};

export default Services;
